import { useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { PackagePlus, Plus } from 'lucide-react'

import type {
    ListNotesParams,
    PackagingLotInReason,
} from '@/lib/api/packaging-lot-api'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { defineListFilters } from '@/components/common/filters/core'
import { selectFilter } from '@/components/common/filters/select-filter'
import { noteStatusFilter } from '@/components/common/filters/presets/note-filters'
import { useListFilters } from '@/components/common/filters/use-list-filters'
import { ListFilterBar } from '@/components/common/ListFilterBar'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import {
    NOTE_CARD_CODE_CLASS,
    NoteListCard,
} from '@/components/common/NoteListCard'
import { Button } from '@/components/ui/button'
import {
    WarehouseNoteStatusBadge,
    warehouseNoteStatusLabel,
} from '@/components/warehouse/note-status-badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useIsMobile } from '@/hooks/use-is-mobile'
import {
    packagingLotKeys,
    usePackagingInNotes,
    usePackagingInNotesInfinite,
} from '@/hooks/use-packaging-lot-query'
import { formatDateTimeVN } from '@/lib/format'
import { StockInNotePrintButton } from '@/components/warehouse/stock-in-note-print-button'
import { packagingLotInNoteToPrintData } from '@/lib/print-adapters/stock-in-note-adapters'
import { packagingLotApi } from '@/lib/api/packaging-lot-api'
import { m } from '@/lib/paraglide/messages'

/** Bộ lọc khai một lần, suy ra: parser URL, mặc định, badge đếm, chip. */
const FILTERS = defineListFilters({
    status: noteStatusFilter(),
    reason: selectFilter<PackagingLotInReason | 'all'>({
        label: m.sl_note_filter_reason,
        defaultValue: 'all',
        options: () => [
            { value: 'all', label: m.sl_note_filter_all_reasons() },
            {
                value: 'from_purchase',
                label: m.pkl_in_reason_from_purchase(),
            },
            { value: 'adjustment', label: m.pkl_in_reason_adjustment() },
        ],
    }),
})

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/packaging-warehouse/stock-in-notes/',
)({
    component: Page,
    validateSearch: FILTERS.validateSearch,
})

function Page() {
    const navigate = useNavigate({
        from: '/packaging-warehouse/stock-in-notes/',
    })

    const { values, barProps, page, setPage } = useListFilters({
        def: FILTERS,
        search: Route.useSearch(),
        onSearchChange: (next, opts) =>
            void navigate({
                search: () => next,
                replace: opts?.replace ?? false,
            }),
        columns: 3,
    })

    // Map bộ lọc → tham số API. Để tường minh ở màn: mỗi màn map một kiểu.
    const apiFilters = useMemo(
        () => ({
            ...(values.status !== 'all' ? { status: values.status } : {}),
            ...(values.reason !== 'all' ? { reason: values.reason } : {}),
        }),
        [values.status, values.reason],
    )

    const params = useMemo<ListNotesParams>(
        () => ({ page, per_page: 50, ...apiFilters }),
        [page, apiFilters],
    )
    const isMobile = useIsMobile()

    const { data, isLoading, isFetching, refetch } = usePackagingInNotes(
        params,
        { enabled: !isMobile },
    )

    // Mobile: infinite (params bỏ page — hook tự quản)
    const infiniteParams = useMemo(
        () => ({ per_page: 50, ...apiFilters }),
        [apiFilters],
    )
    const infinite = usePackagingInNotesInfinite(infiniteParams, {
        enabled: isMobile,
    })
    const mobileNotes = useMemo(
        () => infinite.data?.pages.flatMap((p) => p.data ?? []) ?? [],
        [infinite.data],
    )

    const notes = data?.data ?? []
    const total = data?.meta?.total ?? 0

    // Rỗng vì chưa có dữ liệu khác hẳn rỗng vì bộ lọc quá hẹp — trường hợp thứ
    // hai phải cho người dùng lối thoát ngay tại chỗ (R10).
    const emptyState = (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
            <span className="text-muted-foreground">
                {barProps.activeCount > 0
                    ? m.list_empty_filtered()
                    : m.pkl_note_empty()}
            </span>
            {barProps.activeCount > 0 ? (
                <ClearFilterButton active onClear={barProps.onClear} />
            ) : null}
        </div>
    )

    return (
        <div className="space-y-4 page-pad">
            <ListPageHeader
                icon={<PackagePlus className="size-5 shrink-0" />}
                title={m.pkl_pnk_title()}
                primaryActions={
                    <Button asChild>
                        <Link
                            to="/packaging-warehouse/stock-in-notes/new"
                            className="w-full"
                        >
                            <Plus className="size-4 mr-1" />
                            {m.pkl_note_create_pnk()}
                        </Link>
                    </Button>
                }
            />

            <ListFilterBar
                {...barProps}
                onRefresh={() => void refetch()}
                refreshing={isFetching}
            />

            {/* Mobile: card list + Xem thêm */}
            <div className="space-y-2 md:hidden">
                {infinite.isLoading ? (
                    <div className="py-8 text-center text-muted-foreground">
                        {m.sl_lots_loading()}
                    </div>
                ) : mobileNotes.length === 0 ? (
                    emptyState
                ) : (
                    <>
                        {mobileNotes.map((n) => (
                            <NoteListCard
                                actions={<PrintButton id={n.id} />}
                                key={n.id}
                                onClick={() =>
                                    void navigate({
                                        to: '/packaging-warehouse/stock-in-notes/$id',
                                        params: { id: n.id },
                                    })
                                }
                                code={
                                    <Link
                                        to="/packaging-warehouse/stock-in-notes/$id"
                                        params={{ id: n.id }}
                                        className={NOTE_CARD_CODE_CLASS}
                                    >
                                        {n.code}
                                    </Link>
                                }
                                statusBadge={
                                    <WarehouseNoteStatusBadge
                                        status={n.status}
                                    />
                                }
                                reason={reasonLabel(n.reason)}
                                totalLots={n.items_count ?? 0}
                                createdBy={n.created_by_name ?? undefined}
                                createdAt={n.created_at}
                            />
                        ))}
                        {infinite.hasNextPage && (
                            <Button
                                variant="outline"
                                className="w-full h-11"
                                disabled={infinite.isFetchingNextPage}
                                onClick={() => void infinite.fetchNextPage()}
                            >
                                {infinite.isFetchingNextPage
                                    ? m.list_loading_more()
                                    : m.list_load_more()}
                            </Button>
                        )}
                    </>
                )}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block rounded-md border bg-card overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.sl_note_col_code()}</TableHead>
                            <TableHead>{m.sl_note_col_status()}</TableHead>
                            <TableHead>{m.sl_note_col_reason()}</TableHead>
                            <TableHead className="text-center">
                                {m.sl_note_col_items()}
                            </TableHead>
                            <TableHead>{m.sl_note_col_created_by()}</TableHead>
                            <TableHead>{m.sl_note_col_created_at()}</TableHead>
                            <TableHead className="w-16 text-center">
                                {m.sinp_print_btn()}
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={7}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.sl_lots_loading()}
                                </TableCell>
                            </TableRow>
                        ) : notes.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7}>{emptyState}</TableCell>
                            </TableRow>
                        ) : (
                            notes.map((n) => (
                                <TableRow
                                    key={n.id}
                                    className="cursor-pointer hover:bg-muted/50"
                                    onClick={() =>
                                        void navigate({
                                            to: '/packaging-warehouse/stock-in-notes/$id',
                                            params: { id: n.id },
                                        })
                                    }
                                >
                                    <TableCell className="font-mono text-sm">
                                        <Link
                                            to="/packaging-warehouse/stock-in-notes/$id"
                                            params={{ id: n.id }}
                                            className="text-primary hover:underline"
                                        >
                                            {n.code}
                                        </Link>
                                    </TableCell>
                                    <TableCell>
                                        <WarehouseNoteStatusBadge
                                            status={n.status}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {reasonLabel(n.reason)}
                                    </TableCell>
                                    <TableCell className="text-center">
                                        {n.items_count ?? 0}
                                    </TableCell>
                                    <TableCell>
                                        {n.created_by_name ?? '—'}
                                    </TableCell>
                                    <TableCell className="text-sm text-muted-foreground">
                                        {formatDateTimeVN(n.created_at)}
                                    </TableCell>
                                    <TableCell className="text-center">
                                        <PrintButton id={n.id} />
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination — desktop/tablet only */}
            <div className="hidden md:flex items-center justify-between">
                <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(Math.max(1, page - 1))}
                >
                    {m.sl_pagination_prev()}
                </Button>
                <div className="text-sm text-muted-foreground">
                    {m.sl_pagination_page({
                        page,
                        total: Math.max(1, Math.ceil(total / 50)),
                    })}
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    disabled={!total || page * 50 >= total}
                    onClick={() => setPage(page + 1)}
                >
                    {m.sl_pagination_next()}
                </Button>
            </div>
        </div>
    )
}

function reasonLabel(reason: PackagingLotInReason | string): string {
    if (reason === 'from_purchase') return m.pkl_in_reason_from_purchase()
    if (reason === 'return_from_packaging_process')
        return m.pkl_in_reason_return_from_packaging_process()
    if (reason === 'return_from_order')
        return m.pkl_in_reason_return_from_order()
    if (reason === 'from_order_cancel')
        return m.pkl_in_reason_from_order_cancel()
    if (reason === 'from_stocktake_surplus')
        return m.pl_reason_from_stocktake_surplus()
    return m.pkl_in_reason_adjustment()
}

/** Nút in phiếu nhập kho cho 1 dòng danh sách — tải chi tiết phiếu rồi mới in
 *  vì API danh sách không trả dòng hàng. */
/** Nút in phiếu nhập kho cho 1 dòng danh sách — tải chi tiết phiếu rồi mới in
 *  vì API danh sách không trả dòng hàng. */
function PrintButton({ id }: { id: string }) {
    const qc = useQueryClient()
    return (
        <StockInNotePrintButton
            iconOnly
            loadNote={async () =>
                (
                    await qc.fetchQuery({
                        queryKey: packagingLotKeys.inNoteDetail(id),
                        queryFn: () => packagingLotApi.getInNote(id),
                    })
                ).data ?? null
            }
            toPrintData={(note) =>
                packagingLotInNoteToPrintData(note, {
                    warehouseLabel: m.sinp_wh_packaging(),
                    reasonLabel: reasonLabel(note.reason),
                    statusLabel: warehouseNoteStatusLabel(note.status),
                })
            }
        />
    )
}
